import {
  ResultWithoutExpectedErrors,
  EmptyResultWithoutExpectedErrors,
  IntegrationStatus,
} from '../doppler-types';
import { AxiosInstance, AxiosStatic } from 'axios';
import { AppSession } from './app-session';
import { RefObject } from 'react';

export interface DopplerUserApiClient {
  getContactInformationData(): Promise<ResultWithoutExpectedErrors<ContactInformation>>;
  updateContactInformation(values: any): Promise<EmptyResultWithoutExpectedErrors>;
  getFeatures(): Promise<ResultWithoutExpectedErrors<Features>>;
  getIntegrationsStatus(): Promise<ResultWithoutExpectedErrors<IntegrationsStatus>>;
  getCollaborationInvites(
    pageNumber?: number,
    pageSize?: number,
    search?: string,
  ): Promise<ResultWithoutExpectedErrors<BaseCollectionPage<CollaboratorInvite>>>;
  getAvailableCollaboratorSections(): Promise<
    ResultWithoutExpectedErrors<Array<CollaboratorSection>>
  >;
  sendCollaboratorInvite(
    value: SendCollaboratorInviteData,
  ): Promise<EmptyResultWithoutExpectedErrors>;
  updateCollaborator(value: UpdateCollaboratorData): Promise<EmptyResultWithoutExpectedErrors>;
  cancelCollaboratorInvite(value: string): Promise<EmptyResultWithoutExpectedErrors>;
  updateUserAccountInformation(values: any): Promise<EmptyResultWithoutExpectedErrors>;
  getAccountNotificationSettings(): Promise<
    ResultWithoutExpectedErrors<AccountNotificationSettings>
  >;
  updateAccountNotificationSettings(
    values: AccountNotificationSettings,
  ): Promise<EmptyResultWithoutExpectedErrors>;
}

interface DopplerUserApiConnectionData {
  jwtToken: string;
  email: string;
  idUser: number;
  accountName: string | undefined;
}

export interface ContactInformation {
  email: string;
  firstname: string;
  lastname: string;
  address: string;
  city: string;
  province: string;
  country?: string;
  zipCode: string;
  phone: string;
  company: string;
  industry: string;
  idSecurityQuestion: string;
  answerSecurityQuestion: string;
}

export interface Features {
  contactPolicies: boolean;
  bigQuery: boolean;
}

export interface IntegrationsStatus {
  [key: string]: IntegrationStatus;
}

export interface CollaboratorInvite {
  idUser: number;
  idUserAccount: number | null;
  email: string;
  firstname: string;
  lastname: string;
  invitationDate: string;
  expirationDate: string;
  sections: number[];
  invitationStatus: string;
}

export interface BaseCollectionPage<T> {
  items: T[];
  currentPage: number;
  pageSize: number;
  itemsCount: number;
  pagesCount: number;
}

export interface CollaboratorSection {
  idSection: number;
  name: string;
}

const APPROVER_COLLABORATOR_SECTION_ID = 13;

export interface SendCollaboratorInviteData {
  email: string;
  sections?: number[];
}

export interface UpdateCollaboratorData {
  email: string;
  idUser: number;
  idUserAccount: number | null;
  sections: number[];
}

export interface AccountNotificationSettings {
  weeklyAccountReportEnabled: boolean;
  conversationsMessageNotificationsEnabled: boolean;
  conversationsMessageNotificationSoundEnabled: boolean;
}

export class HttpDopplerUserApiClient implements DopplerUserApiClient {
  private readonly axios: AxiosInstance;
  private readonly baseUrl: string;
  private readonly connectionDataRef: RefObject<AppSession>;

  constructor({
    axiosStatic,
    baseUrl,
    connectionDataRef,
  }: {
    axiosStatic: AxiosStatic;
    baseUrl: string;
    connectionDataRef: RefObject<AppSession>;
  }) {
    this.baseUrl = baseUrl;
    this.axios = axiosStatic.create({
      baseURL: this.baseUrl,
    });
    this.connectionDataRef = connectionDataRef;
  }

  private getDopplerUserApiConnectionData(): DopplerUserApiConnectionData {
    const connectionData = this.connectionDataRef.current;
    if (
      !connectionData ||
      connectionData.status !== 'authenticated' ||
      !connectionData.jwtToken ||
      !connectionData.userData
    ) {
      throw new Error('Doppler User API connection data is not available');
    }
    return {
      jwtToken: connectionData.jwtToken,
      email: connectionData.userData.user.email,
      idUser: connectionData.userData.user.idUser,
      accountName: connectionData.userData.userAccount?.email,
    };
  }

  private mapContactInformation(data: any): ContactInformation {
    return {
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      address: data.address,
      city: data.city,
      province: data.province,
      country: data.country.toLowerCase(),
      zipCode: data.zipCode,
      phone: data.phone,
      company: data.company,
      industry: data.industry,
      idSecurityQuestion: data.idSecurityQuestion,
      answerSecurityQuestion: data.answerSecurityQuestion,
    };
  }

  public async getContactInformationData(): Promise<
    ResultWithoutExpectedErrors<ContactInformation>
  > {
    try {
      const { email, jwtToken } = this.getDopplerUserApiConnectionData();

      const response = await this.axios.request({
        method: 'GET',
        url: `/accounts/${email}/contact-information`,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200 && response.data) {
        return { success: true, value: this.mapContactInformation(response.data) };
      } else {
        return { success: false, error: response.data.title };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async updateContactInformation(values: any): Promise<EmptyResultWithoutExpectedErrors> {
    try {
      const { email, jwtToken } = this.getDopplerUserApiConnectionData();

      const response = await this.axios.request({
        method: 'PUT',
        url: `/accounts/${email}/contact-information`,
        data: values,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        return { success: true };
      } else {
        return { success: false };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  // TODO: it should be removed, the features are available for all users.
  // [Related ticket](https://makingsense.atlassian.net/browse/DOP-1097)
  public async getFeatures(): Promise<ResultWithoutExpectedErrors<Features>> {
    try {
      const { email, jwtToken } = this.getDopplerUserApiConnectionData();
      const response = await this.axios.request({
        method: 'GET',
        url: `/accounts/${email}/features`,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200 && response.data) {
        return { success: true, value: response.data };
      } else {
        return { success: false, error: response.data.title };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async getIntegrationsStatus(): Promise<ResultWithoutExpectedErrors<IntegrationsStatus>> {
    try {
      const { email, jwtToken } = this.getDopplerUserApiConnectionData();
      const response = await this.axios.request({
        method: 'GET',
        url: `/accounts/${email}/integrations/status`,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200 && response.data) {
        return { success: true, value: response.data };
      } else {
        return { success: false, error: response.data.title };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async getAvailableCollaboratorSections(): Promise<
    ResultWithoutExpectedErrors<Array<CollaboratorSection>>
  > {
    try {
      const { email, jwtToken } = this.getDopplerUserApiConnectionData();

      const response = await this.axios.request({
        method: 'GET',
        url: `/accounts/${email}/viewersections`,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200 && response.data) {
        return {
          success: true,
          value: response.data.filter(
            (section: CollaboratorSection) =>
              // "Approver" is a CM campaign approval permission and does not apply in this case.
              section.idSection !== APPROVER_COLLABORATOR_SECTION_ID,
          ),
        };
      } else {
        return { success: false, error: response.data.title };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async sendCollaboratorInvite(
    value: SendCollaboratorInviteData,
  ): Promise<EmptyResultWithoutExpectedErrors> {
    try {
      const { email, jwtToken, idUser } = this.getDopplerUserApiConnectionData();
      const requestData: { email: string; idUser: number; sections?: number[] } = {
        email: value.email,
        idUser: idUser,
      };
      if (value.sections !== undefined) {
        requestData.sections = value.sections;
      }

      const response = await this.axios.request({
        method: 'POST',
        url: `/accounts/${email}/user-invitations`,
        data: requestData,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        return { success: true };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async getCollaborationInvites(
    pageNumber = 0,
    pageSize = 10,
    search?: string,
  ): Promise<ResultWithoutExpectedErrors<BaseCollectionPage<CollaboratorInvite>>> {
    try {
      const { email, jwtToken } = this.getDopplerUserApiConnectionData();
      const normalizedSearch = search?.trim();

      const response = await this.axios.request({
        method: 'GET',
        url: `/accounts/${email}/user-invitations`,
        params: { pageNumber, pageSize, ...(normalizedSearch ? { search: normalizedSearch } : {}) },
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200 && response.data) {
        return { success: true, value: response.data };
      } else {
        return { success: false, error: response.data.title };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async updateCollaborator(
    value: UpdateCollaboratorData,
  ): Promise<EmptyResultWithoutExpectedErrors> {
    try {
      const { email, jwtToken } = this.getDopplerUserApiConnectionData();

      const response = await this.axios.request({
        method: 'PUT',
        url: `/accounts/${email}/update-collaborator-permissions`,
        data: value,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        return { success: true };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async cancelCollaboratorInvite(value: string): Promise<EmptyResultWithoutExpectedErrors> {
    try {
      const { email, jwtToken, idUser } = this.getDopplerUserApiConnectionData();

      const response = await this.axios.request({
        method: 'POST',
        url: `/accounts/${email}/cancel-user-invitation`,
        data: {
          email: value,
          idUser: idUser,
        },
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        return { success: true };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async updateUserAccountInformation(
    values: any,
  ): Promise<EmptyResultWithoutExpectedErrors> {
    try {
      const { accountName, jwtToken } = this.getDopplerUserApiConnectionData();

      if (!accountName) {
        return {
          success: false,
          error: 'invalid user account',
        };
      }

      const response = await this.axios.request({
        method: 'PUT',
        url: `/accounts/user-account/${accountName}/edit`,
        data: values,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        return { success: true };
      } else {
        return {
          success: false,
          message: response.data.message,
          errorCode: response.data.errorCode,
        };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async updateAccountNotificationSettings(
    values: AccountNotificationSettings,
  ): Promise<EmptyResultWithoutExpectedErrors> {
    try {
      const { accountName, jwtToken } = this.getDopplerUserApiConnectionData();

      if (!accountName) {
        return {
          success: false,
          error: 'invalid user account',
        };
      }

      const response = await this.axios.request({
        method: 'PUT',
        url: `/accounts/user-account/${accountName}/account-notifications`,
        data: {
          WeeklyAccountReportEnabled: values.weeklyAccountReportEnabled,
          ConversationsMessageNotificationsEnabled: values.conversationsMessageNotificationsEnabled,
          ConversationsMessageNotificationSoundEnabled:
            values.conversationsMessageNotificationSoundEnabled,
        },
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        return { success: true };
      }

      return { success: false, error: response.data?.message };
    } catch (error) {
      return { success: false, error: error };
    }
  }

  public async getAccountNotificationSettings(): Promise<
    ResultWithoutExpectedErrors<AccountNotificationSettings>
  > {
    try {
      const { accountName, jwtToken } = this.getDopplerUserApiConnectionData();

      if (!accountName) {
        return {
          success: false,
          error: 'invalid user account',
        };
      }

      const response = await this.axios.request({
        method: 'GET',
        url: `/accounts/user-account/${accountName}/account-notifications`,
        headers: { Authorization: `bearer ${jwtToken}` },
      });

      if (response.status === 200 && response.data) {
        return {
          success: true,
          value: {
            weeklyAccountReportEnabled: response.data.weeklyAccountReportEnabled,
            conversationsMessageNotificationsEnabled:
              response.data.conversationsMessageNotificationsEnabled,
            conversationsMessageNotificationSoundEnabled:
              response.data.conversationsMessageNotificationSoundEnabled,
          },
        };
      }

      return { success: false, error: response.data?.title };
    } catch (error) {
      return { success: false, error: error };
    }
  }
}
